// eslint-disable-next-line @typescript-eslint/no-unused-vars
export interface TrappedFocus {
  release: () => void;
  break: () => void;
  // remove: () => void,
  children: HTMLElement[];
  parent: JQuery<HTMLElement>;
  onFocusOut: Function;
}

export class Accessibility {
  static clickAndKeypress(
    ref: JQuery<HTMLElement> | HTMLElement,
    func: (e?: JQuery.Event) => void,
    shouldStopPropagation = true,
    role = 'button',
  ) {
    let jqo: JQuery<HTMLElement>;
    if (ref instanceof jQuery) {
      jqo = ref as JQuery<HTMLElement>;
    } else {
      jqo = $(ref);
    }

    const minDragPx = 10;
    let startX;
    let startY;

    this.addTabIndex(jqo);
    this.addRole(jqo, role);

    jqo.on('mousedown keypress', (e: JQuery.Event) => {
      if (e.type === 'keypress') {
        if (e.which !== 13 && e.which !== 32) {
          return;
        }
        if (shouldStopPropagation) {
          e.stopPropagation();
        }
        func(e);
      }
      startX = e.pageX;
      startY = e.pageY;
    });

    jqo.on('mouseup', (e: JQuery.Event) => {
      const diffX = e.pageX ? Math.abs(e.pageX - startX) : 0;
      const diffY = e.pageY ? Math.abs(e.pageY - startY) : 0;

      if (diffX < minDragPx && diffY < minDragPx) {
        if (shouldStopPropagation) {
          e.stopPropagation();
        }
        func(e);
      }
    });
  }

  static removeEventListeners(ref: JQuery<HTMLElement>[] | JQuery<HTMLElement> | HTMLElement) {
    let jqo: JQuery<HTMLElement>;
    if (Array.isArray(ref)) {
      ref.forEach(r => {
        this.removeEventListeners(r);
      });
      return;
    } else if (ref instanceof jQuery) {
      jqo = ref as JQuery<HTMLElement>;
    } else {
      jqo = $(ref);
    }

    jqo.off('click');
    jqo.off('keypress');
    jqo.off('mousedown');
    jqo.off('mouseup');
    jqo.off('focusout');
  }

  static addKeypress(
    ref: JQuery<HTMLElement> | HTMLElement,
    func?: (e?: JQuery.Event) => void,
    shouldStopPropagation = true,
    role = 'button',
  ) {
    let jqo: JQuery<HTMLElement>;
    if (ref instanceof jQuery) {
      jqo = ref as JQuery<HTMLElement>;
    } else {
      jqo = $(ref);
    }
    this.addTabIndex(jqo);
    this.addRole(jqo, role);

    jqo.on('keypress', (e: JQuery.Event) => {
      if (e.which === 13 || e.which === 32) {
        if (shouldStopPropagation) {
          e.stopPropagation();
        }
        if (typeof func === 'function') {
          func(e);
        } else {
          jqo.trigger('click');
        }
      }
    });
  }

  static addRole(jqo: JQuery<HTMLElement>, role = 'button'): void {
    const roleAttr = jqo.attr('role');
    if (!roleAttr) {
      jqo.attr('role', role);
    }
  }

  static addTabIndex(jqo: JQuery<HTMLElement>, tabIndex = 0) {
    if (!jqo.attr('tabindex') && jqo.prop('tagName') !== 'A') {
      // Only add tabindex if it don't already have one.
      // We don't want to undo -1
      jqo.attr('tabindex', tabIndex);
    }
  }

  static trapFocus(target: JQuery<HTMLElement> | HTMLElement, focusOutCallback?: Function): TrappedFocus {
    // Release any previously trapped focus
    if (this.focusHistoryStack.length > 0) {
      this.focusHistoryStack[this.currentFocusIndex].release();
    }

    let targetJqo: JQuery<HTMLElement>;
    if (target instanceof jQuery) targetJqo = target as JQuery<HTMLElement>;
    else targetJqo = $(target);

    let shouldBreak = false;

    const children = targetJqo.find('[tabindex]:not([tabindex="-1"]), a').toArray();
    let onFocusOut = event => {
      event.stopPropagation();

      if (shouldBreak) {
        shouldBreak = false;
        return;
      }
      /*
                The focus transition between the previous element and the next is
                not complete by the time this event is fired, so we wait 1ms for
                new focus to be established. (I know, I'm not a fan either)
            */
      setTimeout(() => {
        const activeElement = document.activeElement;
        if (activeElement && targetJqo.has(activeElement).length > 0) {
          return;
        }
        if (typeof focusOutCallback === 'function') {
          focusOutCallback(activeElement);
          return;
        }
        const loopback = targetJqo.find("div[tabindex='0']")[0];
        loopback?.focus();
      }, 1);
    };

    children.forEach(child => {
      $(child).on('focusout', onFocusOut);
    });

    let focusedChildren = {
      children: children,
      parent: targetJqo,
      onFocusOut: onFocusOut,
      release: () => {
        children.forEach(child => {
          $(child).off('focusout');
        });
      },
      break: () => {
        shouldBreak = true;
      },
    };

    this.focusHistoryStack.push(focusedChildren);
    this.currentFocusIndex = this.focusHistoryStack.length - 1;

    return focusedChildren;
  }

  static retrapFocus(trappedFocus: TrappedFocus) {
    let children = trappedFocus.children;
    children.forEach(child => {
      $(child).on('focusOut', trappedFocus.onFocusOut);
    });
    if (children.length > 0) {
      children[0].focus();
      Accessibility.focusedElement = children[0];
    }
  }

  static removeFromFocusStack(trappedFocus: TrappedFocus) {
    let index = this.focusHistoryStack.findIndex(tf => {
      return tf === trappedFocus;
    });
    // return if this item has already been removed
    if (index < 0) {
      return;
    }

    let children = trappedFocus.children;
    children.forEach(child => {
      $(child).off('focusout');
    });

    this.focusHistoryStack.splice(index, 1);
    this.currentFocusIndex = this.focusHistoryStack.length > 0 ? this.currentFocusIndex - 1 : null;
    if (this.currentFocusIndex != null) {
      Accessibility.retrapFocus(this.focusHistoryStack[this.currentFocusIndex]);
    }
  }

  static waitForElement(selector: string) {
    return new Promise<Element | null>(resolve => {
      if (document.querySelector(selector)) {
        return resolve(document.querySelector(selector));
      }

      const observer = new MutationObserver(mutations => {
        if (document.querySelector(selector)) {
          resolve(document.querySelector(selector));
          observer.disconnect();
        }
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });
    });
  }

  static parentContainsFocus(element: Element): boolean | null {
    if (element) return element.contains(document.activeElement);
    else return null;
  }

  static focusedElement: HTMLElement;
  static currentFocusIndex: number = null;
  static focusHistoryStack: TrappedFocus[] = [];
  static actionMenuOpen = false;
}
